import validationMessages from "app/utils/form/validationMessages";
import { BOOK_STATUS, PAYMENT_COUPON_TYPE, PAYMENT_METHODS } from "app/constants";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { encryptCreditCard } from "app/utils/creditCardUtils";
import Cookies from "js-cookie";
import { buildPaymentTag } from "app/utils/analytics";
import { getDeviceInfo, parseAmount } from "app/utils/utils";

export const getPaymentErrorMessage = (response, phoneNumber) => {
	let errorMessage = {
		...validationMessages.paymentErrorUnknown,
		values: { telephone: phoneNumber },
	};

	const paymentType = get(response, "data.paymentType");
	const errorStatus = get(response, "data.status");

	if (errorStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY) {
		errorMessage = validationMessages.bookingChildrenAgeDiscrepancy;
	} else if (errorStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY) {
		errorMessage = validationMessages.bookingInfantsAgeDiscrepancy;
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_INFO) {
		errorMessage = validationMessages.paymentErrorCreditCardInfo;
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_REFUSED) {
		if (paymentType && paymentType !== PAYMENT_METHODS.CARD) {
			const messageData = {
				...validationMessages.paymentErrorPartnerRefused,
				values: { telephone: phoneNumber },
			};
			errorMessage = messageData;
		} else {
			errorMessage = validationMessages.paymentErrorCreditCardRefused;
		}
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED) {
		errorMessage = validationMessages.paymentErrorCreditCard3dsRefused;
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR) {
		errorMessage = validationMessages.paymentErrorTechnical;
	} else if (errorStatus === BOOK_STATUS.WARNING_FULL_PROPERTY) {
		errorMessage = validationMessages.paymentErrorFullProperty;
	} else if (errorStatus === BOOK_STATUS.SESSION_EXPIRED) {
		errorMessage = validationMessages.bookingSessionExpired;
	} else if (errorStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED) {
		errorMessage = validationMessages.paymentAgreementRefused;
	} else if (errorStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR) {
		const messageData = {
			...validationMessages.externalPaymentError,
			values: { telephone: phoneNumber },
		};
		errorMessage = messageData;
	}
	return errorMessage;
};

/**
 * on recolte les data du navigateur pour le payload de /book (3ds2)
 */
const buildBrowserInfos = () => {
	const screen = get(window, "screen");
	const navigator = get(window, "navigator");

	const screenWidth = screen ? screen.width : "";
	const screenHeight = screen ? screen.height : "";
	const colorDepth = screen ? screen.colorDepth : "";
	const javaEnabled = navigator ? navigator.javaEnabled() : false;

	const today = new Date();
	const timeZoneOffset = today.getTimezoneOffset();

	let language = "";
	if (window && window.navigator) {
		language = window.navigator.language
			? window.navigator.language
			: window.navigator.browserLanguage; // Else is for IE <+ 10
	}

	return {
		colorDepth,
		screenWidth,
		screenHeight,
		timeZoneOffset,
		javaEnabled,
		language,
	};
};

export const buildPaymentPayload = (values, state, baseUrl) => {
	const name = values.contact.name;
	const address = values.contact.address;
	const insurance = values.insurance;
	const creditCard = values.payment.card;
	const creditCardBCMC = values.payment.cardBCMC;
	const swissBillingFields = values.payment.swissBilling;
	const paymentMode = values.payment.mode;
	const paymentType = values.payment.type;
	const strategy = get(state, "partner.strategy");

	const passengers = values.passengers.map(passenger => {
		return {
			type: passenger.type,
			civility: passenger.name.gender,
			firstName: passenger.name.firstName,
			lastName: passenger.name.lastName,
			frequentFlyerTravellerCardNumber: passenger.frequentFlyerTravellerCardNumber,
			dateOfBirth: {
				day: Number(passenger.birthdate.day),
				year: Number(passenger.birthdate.year),
				month: Number(passenger.birthdate.month) + 1,
			},
		};
	});

	const isPaymentCasino = paymentType === PAYMENT_METHODS.CASINO;

	const payload = {
		strategy,
		code: state.quotation.code,
		customer: {
			civility: name.gender,
			firstName: name.firstName,
			lastName: name.lastName,
			email: values.contact.email,
			phone: values.contact.phone,
			address: {
				address1: address.line1,
				postCode: address.postCode,
				city: address.city,
				country: address.country,
			},
		},
		passengers: passengers,
		paymentType: paymentType, // TODO dupliqué avec payment.type. A supprimer une fois que toutes les brands sont sur le nouvelle api payement
		insurance: insurance,
		baseUrl,
		payment: {
			methodParams: {},
			terms: parseInt(paymentMode, 10),
			type: paymentType,
		},
		browserInfos: buildBrowserInfos(),
	};

	const partnerData = state.partnerData;

	// Ajout du code merch pour le reporting des brands
	if (get(state, "merchandising.current.code")) {
		partnerData.merchCode = state.merchandising.current.code;
	}

	if (!isEmpty(partnerData)) {
		payload.partnerData = partnerData;
	}

	const deviceInfo = getDeviceInfo();
	payload.deviceType = deviceInfo.deviceType;
	payload.userAgent = deviceInfo.userAgent;

	if (isPaymentCasino) {
		const maidenNameValue = creditCard.maidenName;

		if (creditCard.maidenName && maidenNameValue !== "novalidate") {
			payload.customer.maidenName = maidenNameValue;
		}
		payload.customer.dateOfBirth = {
			day: Number(creditCard.birthdate.day),
			year: Number(creditCard.birthdate.year),
			month: Number(creditCard.birthdate.month) + 1,
		};
		payload.customer.zipCodeOfBirth = creditCard.zipCodeOfBirth;
	}

	if (paymentType === PAYMENT_METHODS.CARD) {
		payload.payment.methodParams = {
			encryptedCard: encryptCreditCard({
				holderName: creditCard.holderName,
				expiryMonth: creditCard.expiryMonth,
				expiryYear: "20" + creditCard.expiryYear, // expiryYear est sur 2 digits or il faut 4 digits pour passer la validation adyen
				cvc: creditCard.cvc,
				number: creditCard.number,
				generationtime: state.payment.timestamp,
			}),
		};

		// cas où le champ de saisie du montant est renseigné
		const creditCardAmountToPay = parseAmount(creditCard.amount);

		if (creditCardAmountToPay > 0) {
			payload.payment.amount = creditCardAmountToPay;
		}
	} else if (isPaymentCasino) {
		payload.payment.methodParams = {
			cardNumber: creditCard.number,
			cardCvc: creditCard.cvc,
			cardExpMonth: String(creditCard.expiryMonth),
			cardExpYear: "20" + creditCard.expiryYear, // expiryYear est sur 2 digits or il faut 4 digits pour passer la validation adyen
			cardHolder: creditCard.holderName,
		};
	} else if (paymentType === PAYMENT_METHODS.BCMC) {
		payload.payment.methodParams = {
			encryptedCard: encryptCreditCard({
				holderName: creditCardBCMC.holderName,
				expiryMonth: String(creditCardBCMC.expiryMonth),
				expiryYear: "20" + creditCardBCMC.expiryYear, // expiryYear est sur 2 digits or il faut 4 digits pour passer la validation adyen
				number: creditCardBCMC.number,
				generationtime: state.payment.timestamp,
			}),
		};
	}

	if (paymentType === PAYMENT_METHODS.SWISS_BILLING) {
		payload.customer.dateOfBirth = {
			day: Number(swissBillingFields.birthdate.day),
			year: Number(swissBillingFields.birthdate.year),
			month: Number(swissBillingFields.birthdate.month) + 1,
		};
		payload.payment.methodParams.invoiceType = swissBillingFields.invoiceType;
	} else if (paymentType === PAYMENT_METHODS.IDEAL) {
		payload.payment.methodParams.issuerId = values.payment.issuerId;
	}

	if (
		state.booking.coupon === PAYMENT_COUPON_TYPE.PROMOCODE &&
		state.promotion.promotionValue > 0
	) {
		payload.promoCode = state.promotion.promotionCode;
	} else if (
		state.booking.coupon === PAYMENT_COUPON_TYPE.PROMOCODE &&
		!state.promotion.promotionValue
	) {
		payload.promoCode = "";
	}

	const insuranceData = state.payment.insurances.insurancesItem.find(
		insurance => insurance.code === values.insurance
	);

	const paymentData = {
		insuranceName: insuranceData && insuranceData.name,
		paymentMode,
		paymentType,
	};

	const dataLayer = buildPaymentTag(paymentData);

	dataLayer["._ga"] = Cookies.get("_ga");

	payload.dataLayer = JSON.stringify(dataLayer);

	return payload;
};
